import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface FormData {
  // Housing
  housingSize: string;
  housingType: string;
  energyBill: string | number;
  heatingType: string;
  
  // Transport
  carKm: string | number;
  fuelType: string;
  publicTransport: string;
  flights: string;
  
  // Food
  diet: string;
  localFood: string;
  foodWaste: string;
  
  // Consumption
  shopping: string;
  electronic: string;
  recycling: string;
}

interface ResultsData {
  total: number;
  breakdown: {
    housing: number;
    transport: number;
    food: number;
    consumption: number;
  };
}

const CarbonCalculator: React.FC = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState<number>(1);
  const [results, setResults] = useState<ResultsData | null>(null);
  
  // Form data state with proper typing
  const [formData, setFormData] = useState<FormData>({
    // Housing
    housingSize: "",
    housingType: "",
    energyBill: "",
    heatingType: "",
    
    // Transport
    carKm: "",
    fuelType: "",
    publicTransport: "",
    flights: "",
    
    // Food
    diet: "",
    localFood: "",
    foodWaste: "",
    
    // Consumption
    shopping: "",
    electronic: "",
    recycling: ""
  });
  
  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    const numValue = value === "" ? "" : parseFloat(value);
    setFormData({
      ...formData,
      [name]: numValue
    });
  };
  
  const nextStep = (): void => {
    setStep(step + 1);
  };
  
  const prevStep = (): void => {
    setStep(step - 1);
  };
  
  const calculateResults = (): void => {
    // This is a simplified calculation for demonstration
    // In a real application, this would be much more sophisticated
    
    // Calculate housing emissions (simplified)
    const housingEmissions = 
      (formData.housingSize === "small" ? 1 : formData.housingSize === "medium" ? 2 : 3) *
      (formData.housingType === "apartment" ? 1 : 1.5) *
      (typeof formData.energyBill === "number" 
        ? (formData.energyBill < 50 ? 0.8 : formData.energyBill < 100 ? 1 : 1.3)
        : (formData.energyBill === "low" ? 0.8 : formData.energyBill === "medium" ? 1 : 1.3)) *
      (formData.heatingType === "electric" ? 1 : formData.heatingType === "gas" ? 1.2 : formData.heatingType === "oil" ? 1.5 : 0.7);
    
    // Calculate transport emissions (simplified)
    const transportEmissions = 
      (typeof formData.carKm === "number"
        ? (formData.carKm === 0 ? 0 : formData.carKm < 500 ? 0.5 : formData.carKm < 1000 ? 1.5 : 3)
        : (formData.carKm === "none" ? 0 : formData.carKm === "low" ? 0.5 : formData.carKm === "medium" ? 1.5 : 3)) *
      (formData.fuelType === "electric" ? 0.5 : formData.fuelType === "hybrid" ? 0.8 : formData.fuelType === "diesel" ? 1.3 : 1.2) +
      (formData.publicTransport === "never" ? 0 : formData.publicTransport === "sometimes" ? 0.3 : 0.6) +
      (formData.flights === "none" ? 0 : formData.flights === "1-2" ? 1.5 : 4);
    
    // Calculate food emissions (simplified)
    const foodEmissions = 
      (formData.diet === "vegan" ? 0.5 : formData.diet === "vegetarian" ? 1 : formData.diet === "flexitarian" ? 1.5 : 2) *
      (formData.localFood === "always" ? 0.7 : formData.localFood === "sometimes" ? 1 : 1.3) *
      (formData.foodWaste === "none" ? 0.8 : formData.foodWaste === "little" ? 1 : 1.3);
    
    // Calculate consumption emissions (simplified)
    const consumptionEmissions = 
      (formData.shopping === "rarely" ? 0.5 : formData.shopping === "sometimes" ? 1 : 1.5) +
      (formData.electronic === "rarely" ? 0.5 : formData.electronic === "sometimes" ? 1 : 1.5) *
      (formData.recycling === "always" ? 0.8 : formData.recycling === "sometimes" ? 1 : 1.2);
    
    // Set results
    setResults({
      total: housingEmissions + transportEmissions + foodEmissions + consumptionEmissions,
      breakdown: {
        housing: housingEmissions,
        transport: transportEmissions,
        food: foodEmissions,
        consumption: consumptionEmissions
      }
    });
    
    nextStep();
  };
  
  // Generate personalized recommendations based on form data
  const getRecommendations = (): string[] => {
    const recommendations: string[] = [];
    
    // Housing recommendations
    if (formData.heatingType === "gas" || formData.heatingType === "oil") {
      recommendations.push(t("calculator.recommendations.heating_change") || "Envisagez de changer votre système de chauffage");
    }
    if (formData.energyBill === "high" || (typeof formData.energyBill === "number" && formData.energyBill > 100)) {
      recommendations.push(t("calculator.recommendations.reduce_electricity") || "Réduisez votre consommation d'électricité");
    }
    
    // Transport recommendations
    if ((formData.carKm === "high" || (typeof formData.carKm === "number" && formData.carKm > 1000)) && 
        (formData.fuelType === "gasoline" || formData.fuelType === "diesel")) {
      recommendations.push(t("calculator.recommendations.reduce_car") || "Réduisez l'utilisation de votre voiture");
    }
    if (formData.flights === "many") {
      recommendations.push(t("calculator.recommendations.limit_flights") || "Limitez vos voyages en avion");
    }
    if (formData.publicTransport === "never") {
      recommendations.push(t("calculator.recommendations.use_public_transport") || "Utilisez les transports en commun");
    }
    
    // Food recommendations
    if (formData.diet === "meat_daily") {
      recommendations.push(t("calculator.recommendations.reduce_meat") || "Réduisez votre consommation de viande");
    }
    if (formData.localFood === "rarely") {
      recommendations.push(t("calculator.recommendations.local_food") || "Privilégiez les produits locaux et de saison");
    }
    if (formData.foodWaste === "much") {
      recommendations.push(t("calculator.recommendations.reduce_waste") || "Réduisez le gaspillage alimentaire");
    }
    
    // Consumption recommendations
    if (formData.shopping === "often") {
      recommendations.push(t("calculator.recommendations.responsible_consumption") || "Adoptez une consommation plus responsable");
    }
    if (formData.electronic === "often") {
      recommendations.push(t("calculator.recommendations.extend_electronics") || "Prolongez la durée de vie de vos appareils électroniques");
    }
    if (formData.recycling === "never") {
      recommendations.push(t("calculator.recommendations.start_recycling") || "Commencez à recycler vos déchets");
    }
    
    // If no specific recommendations, add general ones
    if (recommendations.length === 0) {
      recommendations.push(t("calculator.recommendations.continue_efforts") || "Continuez vos efforts pour réduire votre empreinte carbone");
      recommendations.push(t("calculator.recommendations.join_initiatives") || "Rejoignez des initiatives locales pour le climat");
    }
    
    return recommendations;
  };
  
  const renderStep = () => {
    switch(step) {
      case 1:
        return (
          <div className="bg-white p-8 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold text-mypa-blue mb-6">{t("calculator.housing.title") || "Logement"}</h2>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">{t("calculator.housing.size") || "Taille du logement"}</label>
              <select 
                name="housingSize" 
                value={formData.housingSize} 
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green focus:border-transparent"
              >
                <option value="">{t("calculator.select") || "Sélectionner"}</option>
                <option value="small">{t("calculator.housing.size_small") || "Petit (< 50m²)"}</option>
                <option value="medium">{t("calculator.housing.size_medium") || "Moyen (50-100m²)"}</option>
                <option value="large">{t("calculator.housing.size_large") || "Grand (> 100m²)"}</option>
              </select>
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">{t("calculator.housing.type") || "Type de logement"}</label>
              <select 
                name="housingType" 
                value={formData.housingType} 
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green focus:border-transparent"
              >
                <option value="">{t("calculator.select") || "Sélectionner"}</option>
                <option value="apartment">{t("calculator.housing.type_apartment") || "Appartement"}</option>
                <option value="house">{t("calculator.housing.type_house") || "Maison"}</option>
              </select>
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">{t("calculator.housing.energy") || "Facture d'électricité mensuelle"}</label>
              <input 
                type="number" 
                name="energyBill" 
                value={formData.energyBill} 
                onChange={handleNumberInputChange}
                placeholder={t("calculator.housing.energy_placeholder") || "Montant en euros"}
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">{t("calculator.housing.energy_help") || "Entrez le montant approximatif de votre facture mensuelle d'électricité"}</p>
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">{t("calculator.housing.heating") || "Type de chauffage"}</label>
              <select 
                name="heatingType" 
                value={formData.heatingType} 
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green focus:border-transparent"
              >
                <option value="">{t("calculator.select") || "Sélectionner"}</option>
                <option value="electric">{t("calculator.housing.heating_electric") || "Électrique"}</option>
                <option value="gas">{t("calculator.housing.heating_gas") || "Gaz"}</option>
                <option value="oil">{t("calculator.housing.heating_oil") || "Fioul"}</option>
                <option value="renewable">{t("calculator.housing.heating_renewable") || "Énergies renouvelables"}</option>
              </select>
            </div>
            
            <div className="flex justify-end">
              <button 
                onClick={nextStep} 
                className="bg-mypa-green text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
                disabled={!formData.housingSize || !formData.housingType || !formData.energyBill || !formData.heatingType}
              >
                {t("calculator.next") || "Suivant"}
              </button>
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="bg-white p-8 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold text-mypa-blue mb-6">{t("calculator.transport.title") || "Transport"}</h2>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">{t("calculator.transport.car") || "Kilométrage mensuel en voiture"}</label>
              <input 
                type="number" 
                name="carKm" 
                value={formData.carKm} 
                onChange={handleNumberInputChange}
                placeholder={t("calculator.transport.car_placeholder") || "Kilomètres par mois"}
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">{t("calculator.transport.car_help") || "Entrez 0 si vous n'utilisez pas de voiture"}</p>
            </div>
            
            {(formData.carKm !== "" && formData.carKm !== 0) && (
              <div className="mb-6">
                <label className="block text-gray-700 mb-2">{t("calculator.transport.fuel") || "Type de carburant"}</label>
                <select 
                  name="fuelType" 
                  value={formData.fuelType} 
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green focus:border-transparent"
                >
                  <option value="">{t("calculator.select") || "Sélectionner"}</option>
                  <option value="gasoline">{t("calculator.transport.fuel_gasoline") || "Essence"}</option>
                  <option value="diesel">{t("calculator.transport.fuel_diesel") || "Diesel"}</option>
                  <option value="hybrid">{t("calculator.transport.fuel_hybrid") || "Hybride"}</option>
                  <option value="electric">{t("calculator.transport.fuel_electric") || "Électrique"}</option>
                </select>
              </div>
            )}
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">{t("calculator.transport.public") || "Utilisation des transports en commun"}</label>
              <select 
                name="publicTransport" 
                value={formData.publicTransport} 
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green focus:border-transparent"
              >
                <option value="">{t("calculator.select") || "Sélectionner"}</option>
                <option value="never">{t("calculator.transport.public_never") || "Jamais"}</option>
                <option value="sometimes">{t("calculator.transport.public_sometimes") || "Parfois"}</option>
                <option value="often">{t("calculator.transport.public_often") || "Régulièrement"}</option>
              </select>
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">{t("calculator.transport.plane") || "Voyages en avion par an"}</label>
              <select 
                name="flights" 
                value={formData.flights} 
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green focus:border-transparent"
              >
                <option value="">{t("calculator.select") || "Sélectionner"}</option>
                <option value="none">{t("calculator.transport.plane_none") || "Aucun"}</option>
                <option value="1-2">{t("calculator.transport.plane_few") || "1-2 vols"}</option>
                <option value="many">{t("calculator.transport.plane_many") || "3 vols ou plus"}</option>
              </select>
            </div>
            
            <div className="flex justify-between">
              <button 
                onClick={prevStep} 
                className="border border-mypa-blue text-mypa-blue px-6 py-3 rounded-lg font-medium hover:bg-mypa-blue hover:bg-opacity-10 transition-colors"
              >
                {t("calculator.previous") || "Précédent"}
              </button>
              <button 
                onClick={nextStep} 
                className="bg-mypa-green text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
                disabled={formData.carKm === "" || ((formData.carKm !== 0 && formData.carKm !== "none") && !formData.fuelType) || !formData.publicTransport || !formData.flights}
              >
                {t("calculator.next") || "Suivant"}
              </button>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="bg-white p-8 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold text-mypa-blue mb-6">{t("calculator.food.title") || "Alimentation"}</h2>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">{t("calculator.food.diet") || "Régime alimentaire"}</label>
              <select 
                name="diet" 
                value={formData.diet} 
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green focus:border-transparent"
              >
                <option value="">{t("calculator.select") || "Sélectionner"}</option>
                <option value="vegan">{t("calculator.food.diet_vegan") || "Végétalien"}</option>
                <option value="vegetarian">{t("calculator.food.diet_vegetarian") || "Végétarien"}</option>
                <option value="flexitarian">{t("calculator.food.diet_flexitarian") || "Flexitarien"}</option>
                <option value="meat_daily">{t("calculator.food.diet_meat") || "Viande quotidienne"}</option>
              </select>
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">{t("calculator.food.local") || "Consommation de produits locaux"}</label>
              <select 
                name="localFood" 
                value={formData.localFood} 
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green focus:border-transparent"
              >
                <option value="">{t("calculator.select") || "Sélectionner"}</option>
                <option value="always">{t("calculator.food.local_always") || "Toujours"}</option>
                <option value="sometimes">{t("calculator.food.local_sometimes") || "Parfois"}</option>
                <option value="rarely">{t("calculator.food.local_rarely") || "Rarement"}</option>
              </select>
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">{t("calculator.food.waste") || "Gaspillage alimentaire"}</label>
              <select 
                name="foodWaste" 
                value={formData.foodWaste} 
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green focus:border-transparent"
              >
                <option value="">{t("calculator.select") || "Sélectionner"}</option>
                <option value="none">{t("calculator.food.waste_none") || "Aucun"}</option>
                <option value="little">{t("calculator.food.waste_little") || "Un peu"}</option>
                <option value="much">{t("calculator.food.waste_much") || "Beaucoup"}</option>
              </select>
            </div>
            
            <div className="flex justify-between">
              <button 
                onClick={prevStep} 
                className="border border-mypa-blue text-mypa-blue px-6 py-3 rounded-lg font-medium hover:bg-mypa-blue hover:bg-opacity-10 transition-colors"
              >
                {t("calculator.previous") || "Précédent"}
              </button>
              <button 
                onClick={nextStep} 
                className="bg-mypa-green text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
                disabled={!formData.diet || !formData.localFood || !formData.foodWaste}
              >
                {t("calculator.next") || "Suivant"}
              </button>
            </div>
          </div>
        );
        
      case 4:
        return (
          <div className="bg-white p-8 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold text-mypa-blue mb-6">{t("calculator.consumption.title") || "Consommation"}</h2>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">{t("calculator.consumption.shopping") || "Achats de vêtements et biens"}</label>
              <select 
                name="shopping" 
                value={formData.shopping} 
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green focus:border-transparent"
              >
                <option value="">{t("calculator.select") || "Sélectionner"}</option>
                <option value="rarely">{t("calculator.consumption.shopping_rarely") || "Rarement"}</option>
                <option value="sometimes">{t("calculator.consumption.shopping_sometimes") || "Parfois"}</option>
                <option value="often">{t("calculator.consumption.shopping_often") || "Souvent"}</option>
              </select>
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">{t("calculator.consumption.electronic") || "Achats d'appareils électroniques"}</label>
              <select 
                name="electronic" 
                value={formData.electronic} 
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green focus:border-transparent"
              >
                <option value="">{t("calculator.select") || "Sélectionner"}</option>
                <option value="rarely">{t("calculator.consumption.electronic_rarely") || "Rarement"}</option>
                <option value="sometimes">{t("calculator.consumption.electronic_sometimes") || "Parfois"}</option>
                <option value="often">{t("calculator.consumption.electronic_often") || "Souvent"}</option>
              </select>
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">{t("calculator.consumption.recycling") || "Recyclage"}</label>
              <select 
                name="recycling" 
                value={formData.recycling} 
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green focus:border-transparent"
              >
                <option value="">{t("calculator.select") || "Sélectionner"}</option>
                <option value="always">{t("calculator.consumption.recycling_always") || "Toujours"}</option>
                <option value="sometimes">{t("calculator.consumption.recycling_sometimes") || "Parfois"}</option>
                <option value="never">{t("calculator.consumption.recycling_never") || "Jamais"}</option>
              </select>
            </div>
            
            <div className="flex justify-between">
              <button 
                onClick={prevStep} 
                className="border border-mypa-blue text-mypa-blue px-6 py-3 rounded-lg font-medium hover:bg-mypa-blue hover:bg-opacity-10 transition-colors"
              >
                {t("calculator.previous") || "Précédent"}
              </button>
              <button 
                onClick={calculateResults} 
                className="bg-mypa-green text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
                disabled={!formData.shopping || !formData.electronic || !formData.recycling}
              >
                {t("calculator.calculate") || "Calculer"}
              </button>
            </div>
          </div>
        );
        
      case 5:
        return (
          <div className="bg-white p-8 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold text-mypa-blue mb-6">{t("calculator.results.title") || "Résultats"}</h2>
            
            {results && (
              <>
                <div className="text-center mb-8">
                  <p className="text-lg mb-2">{t("calculator.results.footprint") || "Votre empreinte carbone annuelle"}</p>
                  <p className="text-4xl font-bold text-mypa-green">{results.total.toFixed(1)} tCO2e/an</p>
                  <p className="text-sm text-gray-500 mt-2">{t("calculator.results.average") || "Moyenne mondiale :"} 11.2 tCO2e/an</p>
                </div>
                
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-mypa-blue mb-4">{t("calculator.results.breakdown") || "Répartition par catégorie"}</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span>{t("calculator.steps.housing") || "Logement"}</span>
                        <span>{results.breakdown.housing.toFixed(1)} tCO2e</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-mypa-green h-2.5 rounded-full" style={{ width: `${(results.breakdown.housing / results.total) * 100}%` }}></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span>{t("calculator.steps.transport") || "Transport"}</span>
                        <span>{results.breakdown.transport.toFixed(1)} tCO2e</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-mypa-blue h-2.5 rounded-full" style={{ width: `${(results.breakdown.transport / results.total) * 100}%` }}></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span>{t("calculator.steps.food") || "Alimentation"}</span>
                        <span>{results.breakdown.food.toFixed(1)} tCO2e</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-yellow-500 h-2.5 rounded-full" style={{ width: `${(results.breakdown.food / results.total) * 100}%` }}></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span>{t("calculator.steps.consumption") || "Consommation"}</span>
                        <span>{results.breakdown.consumption.toFixed(1)} tCO2e</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-purple-500 h-2.5 rounded-full" style={{ width: `${(results.breakdown.consumption / results.total) * 100}%` }}></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-mypa-blue mb-4">{t("calculator.results.recommendations") || "Recommandations personnalisées"}</h3>
                  
                  <ul className="space-y-2">
                    {getRecommendations().map((recommendation, index) => (
                      <li key={index} className="flex items-start">
                        <div className="bg-mypa-green rounded-full p-1 mr-3 mt-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span>{recommendation}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="text-center">
                  <p className="mb-4">{t("calculator.results.contact_text") || "Contactez-nous pour obtenir un accompagnement personnalisé"}</p>
                  <a href="/contact" className="bg-mypa-blue text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors inline-block">
                    {t("common.contact_us") || "Nous contacter"}
                  </a>
                </div>
              </>
            )}
            
            <div className="flex justify-start mt-8">
              <button 
                onClick={() => setStep(1)} 
                className="border border-mypa-blue text-mypa-blue px-6 py-3 rounded-lg font-medium hover:bg-mypa-blue hover:bg-opacity-10 transition-colors"
              >
                {t("calculator.restart") || "Recommencer"}
              </button>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="container-custom section-padding">
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-mypa-blue mb-4">
          {t("calculator.title") || "Calculateur d'empreinte carbone"}
        </h1>
        <p className="text-xl max-w-3xl mx-auto">
          {t("calculator.subtitle") || "Estimez votre impact environnemental personnel"}
        </p>
        <p className="mt-4 max-w-2xl mx-auto">
          {t("calculator.intro") || "Répondez à quelques questions simples pour obtenir une estimation de votre empreinte carbone annuelle et des recommandations personnalisées."}
        </p>
      </div>
      
      {/* Progress Steps */}
      <div className="flex justify-center mb-12">
        <div className="flex items-center w-full max-w-3xl">
          <div className={`flex flex-col items-center ${step >= 1 ? 'text-mypa-green' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-mypa-green text-white' : 'bg-gray-200'}`}>
              1
            </div>
            <span className="text-sm mt-1">{t("calculator.steps.housing") || "Logement"}</span>
          </div>
          
          <div className={`flex-1 h-1 mx-2 ${step >= 2 ? 'bg-mypa-green' : 'bg-gray-200'}`}></div>
          
          <div className={`flex flex-col items-center ${step >= 2 ? 'text-mypa-green' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-mypa-green text-white' : 'bg-gray-200'}`}>
              2
            </div>
            <span className="text-sm mt-1">{t("calculator.steps.transport") || "Transport"}</span>
          </div>
          
          <div className={`flex-1 h-1 mx-2 ${step >= 3 ? 'bg-mypa-green' : 'bg-gray-200'}`}></div>
          
          <div className={`flex flex-col items-center ${step >= 3 ? 'text-mypa-green' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-mypa-green text-white' : 'bg-gray-200'}`}>
              3
            </div>
            <span className="text-sm mt-1">{t("calculator.steps.food") || "Alimentation"}</span>
          </div>
          
          <div className={`flex-1 h-1 mx-2 ${step >= 4 ? 'bg-mypa-green' : 'bg-gray-200'}`}></div>
          
          <div className={`flex flex-col items-center ${step >= 4 ? 'text-mypa-green' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 4 ? 'bg-mypa-green text-white' : 'bg-gray-200'}`}>
              4
            </div>
            <span className="text-sm mt-1">{t("calculator.steps.consumption") || "Consommation"}</span>
          </div>
          
          <div className={`flex-1 h-1 mx-2 ${step >= 5 ? 'bg-mypa-green' : 'bg-gray-200'}`}></div>
          
          <div className={`flex flex-col items-center ${step >= 5 ? 'text-mypa-green' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 5 ? 'bg-mypa-green text-white' : 'bg-gray-200'}`}>
              5
            </div>
            <span className="text-sm mt-1">{t("calculator.steps.results") || "Résultats"}</span>
          </div>
        </div>
      </div>
      
      {/* Form Steps */}
      <div className="max-w-3xl mx-auto">
        {renderStep()}
      </div>
    </div>
  );
};

export default CarbonCalculator;
